import React, { useState, useRef, useCallback } from "react";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * ImportExcelModal - Modal de importación masiva de configuración de inventario vía Excel.
 *
 * Props:
 * - open: boolean - Si el modal está visible
 * - onClose: () => void - Callback para cerrar el modal
 * - token: string - Token JWT para autenticación
 * - onSuccess: () => void - Callback tras importación exitosa (para refrescar datos)
 */
export function ImportExcelModal({ open, onClose, token, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setFile(null);
    setUploading(false);
    setResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    setError(null);
    setResult(null);

    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validar extensión .xlsx
    const validExtensions = [".xlsx"];
    const fileName = selectedFile.name.toLowerCase();
    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    );

    if (!hasValidExtension) {
      setError("Solo se aceptan archivos con extensión .xlsx");
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setFile(selectedFile);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file || uploading) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${API_URL}/bi/configuracion-inventario/import`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errText = await response.text().catch(() => "Error desconocido");
        throw new Error(errText || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setResult(data);

      // Refrescar datos si hubo importaciones exitosas
      if (data.imported > 0 && onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || "Error al importar el archivo");
    } finally {
      setUploading(false);
    }
  }, [file, uploading, token, onSuccess]);

  if (!open) return null;

  return (
    <div className="z-[400] fixed top-0 left-0 h-screen w-screen">
      {/* Overlay */}
      <div
        onClick={handleClose}
        className="bg-baseGray/70 fixed top-0 left-0 h-screen w-screen z-40"
      />

      {/* Modal content */}
      <div className="z-50 fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-[600px] max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            Importar Excel - Configuración de Inventario
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Formato esperado */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-800 mb-2">
              Formato esperado del archivo Excel (.xlsx):
            </p>
            <div className="overflow-x-auto">
              <table className="text-xs text-blue-700 border-collapse">
                <thead>
                  <tr className="bg-blue-100">
                    <th className="border border-blue-200 px-3 py-1">
                      idProducto
                    </th>
                    <th className="border border-blue-200 px-3 py-1">
                      idBodega
                    </th>
                    <th className="border border-blue-200 px-3 py-1">
                      inventarioMinimo
                    </th>
                    <th className="border border-blue-200 px-3 py-1">
                      inventarioObjetivo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-blue-200 px-3 py-1">101</td>
                    <td className="border border-blue-200 px-3 py-1">1</td>
                    <td className="border border-blue-200 px-3 py-1">5</td>
                    <td className="border border-blue-200 px-3 py-1">20</td>
                  </tr>
                  <tr>
                    <td className="border border-blue-200 px-3 py-1">102</td>
                    <td className="border border-blue-200 px-3 py-1">2</td>
                    <td className="border border-blue-200 px-3 py-1">3</td>
                    <td className="border border-blue-200 px-3 py-1">15</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              La primera fila debe contener los encabezados exactos.
              inventarioMinimo: 0-99,999 | inventarioObjetivo: 1-99,999 |
              Mínimo debe ser menor al objetivo.
            </p>
          </div>

          {/* File input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar archivo
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple file:text-white hover:file:bg-purple/90 file:cursor-pointer disabled:opacity-50"
            />
            {file && (
              <p className="text-xs text-gray-500 mt-1">
                Archivo seleccionado: {file.name} (
                {(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Upload results */}
          {result && (
            <div className="space-y-3">
              {/* Summary */}
              <div
                className={`px-4 py-3 rounded-lg text-sm ${
                  result.failed > 0
                    ? "bg-yellow-50 border border-yellow-200 text-yellow-800"
                    : "bg-green-50 border border-green-200 text-green-800"
                }`}
              >
                <p className="font-medium">
                  {result.imported} fila{result.imported !== 1 ? "s" : ""}{" "}
                  importada{result.imported !== 1 ? "s" : ""}, {result.failed}{" "}
                  error{result.failed !== 1 ? "es" : ""}
                </p>
                {result.totalRows != null && (
                  <p className="text-xs mt-1 opacity-75">
                    Total de filas procesadas: {result.totalRows}
                  </p>
                )}
              </div>

              {/* Error details */}
              {result.results &&
                result.results.filter((r) => !r.success).length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <p className="text-xs font-medium text-gray-700 mb-2">
                      Detalle de errores:
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      {result.results
                        .filter((r) => !r.success)
                        .map((r, idx) => (
                          <li key={idx} className="flex gap-2">
                            <span className="font-medium text-red-600">
                              Fila {r.row}:
                            </span>
                            <span>{r.error}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="text-sm font-medium text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {result ? "Cerrar" : "Cancelar"}
          </button>
          {!result && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="inline-flex items-center gap-2 bg-purple text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-purple/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Importando...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                  Importar
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportExcelModal;
